import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PlaceIcon from '@mui/icons-material/Place';

import { useSearchLocationsQuery } from '../api/apiSlice';
import { LocationSuggestion } from '../types/api';

interface LocationAutocompleteProps {
    value: string;
    /** Fires on every keystroke and on free-text entry. */
    onTextChange: (text: string) => void;
    /** Fires when the user picks a suggestion (carries coordinates + city). */
    onPick: (suggestion: LocationSuggestion) => void;
    label: string;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    onBlur?: () => void;
}

function LocationAutocomplete({
    value,
    onTextChange,
    onPick,
    label,
    required,
    error,
    helperText,
    onBlur,
}: LocationAutocompleteProps) {
    const { i18n } = useTranslation();
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), 350);
        return () => clearTimeout(id);
    }, [value]);

    const trimmed = debounced.trim();
    const { data = [], isFetching } = useSearchLocationsQuery(
        { q: trimmed, lang: i18n.language === 'fr' ? 'fr' : 'en' },
        { skip: trimmed.length < 3 },
    );

    // Keep whatever the user typed as a selectable "free text" option so the
    // Autocomplete doesn't fight a value that isn't in the list.
    const options = useMemo(() => data, [data]);

    return (
        <Autocomplete<LocationSuggestion, false, false, true>
            freeSolo
            fullWidth
            options={options}
            filterOptions={(x) => x}
            inputValue={value}
            loading={isFetching}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            isOptionEqualToValue={(option, val) =>
                typeof option !== 'string' && typeof val !== 'string' && option.label === val.label
            }
            onInputChange={(_e, newInput, reason) => {
                if (reason === 'input' || reason === 'clear') onTextChange(newInput);
            }}
            onChange={(_e, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                    onTextChange(newValue.name);
                    onPick(newValue);
                } else if (typeof newValue === 'string') {
                    onTextChange(newValue);
                }
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.label}>
                    <PlaceIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{option.name}</Typography>
                        {option.label !== option.name && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {option.label}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    onBlur={onBlur}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

export default LocationAutocomplete;
